/**
 * SUPPLEMENTAL LETTERS PAGE
 * 
 * This component allows users to generate specialized dispute letters
 * for specific account types AFTER the main dispute letters are generated.
 * 
 * Letter Types:
 * - IDENTITY THEFT SECTION: Accounts Re-Added, Verified Accounts, Child Support, Student Loans
 * - LATE PAYMENTS SECTION: Late Payments (Billing Error), Auto Loan Lates
 * - INQUIRY SECTION: Inquiry Removal (secondary to existing inquiry dispute)
 * - BANKRUPTCY SECTION: Bankruptcy Removal
 * 
 * Features:
 * - Checkboxes to select which letters to generate
 * - Dropdown to select accounts for each letter
 * - Manual account entry option
 * - Auto-fills client info, date, and signature
 * - Generates letters and adds to dispute packet
 */

import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import {
  ChevronDown,
  ChevronUp,
  FileText,
  Plus,
  Trash2,
  Download,
  Loader2,
  CheckCircle2,
  AlertCircle,
  RefreshCcw,
  Scale,
  CreditCard,
  Clock,
  Search,
  GraduationCap,
  Baby,
  Shield,
  X
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Letter template definitions
const SUPPLEMENTAL_LETTER_TYPES = {
  // ═══════════════════════════════════════════════════════════════════════════
  // IDENTITY THEFT SECTION LETTERS
  // ═══════════════════════════════════════════════════════════════════════════
  accounts_readded: {
    id: 'accounts_readded',
    title: 'Accounts Re-Added',
    description: 'For accounts that were previously removed but reinserted onto credit report',
    icon: RefreshCcw,
    section: 'identity_theft',
    codes: ['15 U.S.C. § 1681i(a)(5)(B)(ii)', '15 U.S.C. § 1681i(a)(5)(B)(iii)', '15 U.S.C. § 1681n', '15 U.S.C. § 1681o'],
    color: 'text-red-400',
    bgColor: 'bg-red-500/10',
    borderColor: 'border-red-500/30'
  },
  verified_accounts: {
    id: 'verified_accounts',
    title: 'Verified Accounts',
    description: 'For accounts marked as "verified" but contain inaccurate information',
    icon: Shield,
    section: 'identity_theft',
    codes: ['15 U.S.C. § 1605', '15 U.S.C. § 1681e(b)', '15 U.S.C. § 1681i'],
    color: 'text-amber-400',
    bgColor: 'bg-amber-500/10',
    borderColor: 'border-amber-500/30'
  },
  child_support: {
    id: 'child_support',
    title: 'Child Support',
    description: 'For disputing child support accounts reported without proper verification',
    icon: Baby,
    section: 'identity_theft',
    codes: ['15 U.S.C. § 1681e(b)', '15 U.S.C. § 1681i(a)(5)', '15 U.S.C. § 1681i(a)(7)'],
    color: 'text-purple-400',
    bgColor: 'bg-purple-500/10',
    borderColor: 'border-purple-500/30'
  },
  student_loans: {
    id: 'student_loans',
    title: 'Student Loans',
    description: 'For student loan accounts reported without FERPA authorization',
    icon: GraduationCap,
    section: 'identity_theft',
    codes: ['FERPA 20 U.S.C. § 1232g', '15 U.S.C. § 1681e(b)', '15 U.S.C. § 1681i(a)(5)', '15 U.S.C. § 1681i(a)(7)'],
    color: 'text-blue-400',
    bgColor: 'bg-blue-500/10',
    borderColor: 'border-blue-500/30'
  },
  
  // ═══════════════════════════════════════════════════════════════════════════
  // LATE PAYMENTS SECTION LETTERS
  // ═══════════════════════════════════════════════════════════════════════════
  late_payments: {
    id: 'late_payments',
    title: 'Late Payments (Billing Error)',
    description: 'For disputing late payments as billing errors under FCBA',
    icon: Clock,
    section: 'late_payments',
    codes: ['15 U.S.C. § 1666', '15 U.S.C. § 1681e(b)', '15 U.S.C. § 1681i', '15 U.S.C. § 1681n', '15 U.S.C. § 1681o'],
    color: 'text-orange-400',
    bgColor: 'bg-orange-500/10',
    borderColor: 'border-orange-500/30'
  },
  auto_loan_lates: {
    id: 'auto_loan_lates',
    title: 'Auto Loan Late Payments',
    description: 'For disputing late payments on auto loans due to TILA violations',
    icon: CreditCard,
    section: 'late_payments',
    codes: ['15 U.S.C. § 1605', '15 U.S.C. § 1681e(b)', '15 U.S.C. § 1681i', '15 U.S.C. § 1681n', '15 U.S.C. § 1681o'],
    color: 'text-cyan-400',
    bgColor: 'bg-cyan-500/10',
    borderColor: 'border-cyan-500/30'
  },
  
  // ═══════════════════════════════════════════════════════════════════════════
  // INQUIRY SECTION LETTERS
  // ═══════════════════════════════════════════════════════════════════════════
  inquiry_removal: {
    id: 'inquiry_removal',
    title: 'Inquiry Removal',
    description: 'Secondary letter for unauthorized credit inquiries',
    icon: Search,
    section: 'inquiries',
    codes: ['15 U.S.C. § 1681b', '15 U.S.C. § 1681i', '15 U.S.C. § 1681n', '15 U.S.C. § 1681o'],
    color: 'text-green-400',
    bgColor: 'bg-green-500/10',
    borderColor: 'border-green-500/30'
  },
  
  // ═══════════════════════════════════════════════════════════════════════════
  // BANKRUPTCY SECTION LETTERS
  // ═══════════════════════════════════════════════════════════════════════════
  bankruptcy_removal: {
    id: 'bankruptcy_removal',
    title: 'Bankruptcy Removal',
    description: 'For disputing bankruptcy reporting without proper verification',
    icon: Scale,
    section: 'bankruptcy',
    codes: ['15 U.S.C. § 1681e(b)', '15 U.S.C. § 1681i'],
    color: 'text-rose-400',
    bgColor: 'bg-rose-500/10',
    borderColor: 'border-rose-500/30'
  }
};

// Group letters by section
const LETTER_SECTIONS = {
  identity_theft: {
    title: 'Identity Theft Letters',
    description: 'Use these letters when disputing with identity theft claims',
    letters: ['accounts_readded', 'verified_accounts', 'child_support', 'student_loans'],
    color: 'text-red-400',
    bgColor: 'bg-red-900/20'
  },
  late_payments: {
    title: 'Late Payment Letters',
    description: 'Use these letters when disputing late payment inaccuracies',
    letters: ['late_payments', 'auto_loan_lates'],
    color: 'text-orange-400',
    bgColor: 'bg-orange-900/20'
  },
  inquiries: {
    title: 'Inquiry Letters',
    description: 'Secondary letter to support inquiry disputes',
    letters: ['inquiry_removal'],
    color: 'text-green-400',
    bgColor: 'bg-green-900/20'
  },
  bankruptcy: {
    title: 'Bankruptcy Letters',
    description: 'Use when disputing fraudulent bankruptcy reporting',
    letters: ['bankruptcy_removal'],
    color: 'text-rose-400',
    bgColor: 'bg-rose-900/20'
  }
};

export default function SupplementalLettersPage({
  clientId,
  clientData,
  signatureData,
  availableAccounts = [],
  availableInquiries = [],
  hasBankruptcy = false,
  disputeType = 'identity_theft',
  onComplete,
  onLettersGenerated
}) {
  // Track which letters are selected
  const [selectedLetters, setSelectedLetters] = useState({});
  
  // Track accounts selected for each letter type
  const [letterAccounts, setLetterAccounts] = useState({});
  
  // Track manual accounts for each letter
  const [manualAccounts, setManualAccounts] = useState({});
  
  // Track expanded sections
  const [expandedSections, setExpandedSections] = useState({
    identity_theft: true,
    late_payments: true,
    inquiries: true,
    bankruptcy: true
  });
  
  // Track expanded letter cards
  const [expandedLetters, setExpandedLetters] = useState({});
  
  // Track generated letters
  const [generatedLetters, setGeneratedLetters] = useState({});
  
  // Loading states
  const [generating, setGenerating] = useState(null);
  const [downloadingAll, setDownloadingAll] = useState(false);

  // Auto-suggest relevant letters based on account types and client data
  // This runs once when component mounts to pre-select relevant letters
  useEffect(() => {
    const suggestions = {};
    const autoExpandSections = {};
    
    // Check for student loans in accounts
    const hasStudentLoans = availableAccounts.some(acc => {
      const name = (acc.creditor_name || acc.creditor || acc.name || '').toLowerCase();
      return name.includes('student') || name.includes('navient') || name.includes('nelnet') || 
             name.includes('fedloan') || name.includes('great lakes') || name.includes('mohela') ||
             name.includes('dept of ed') || name.includes('department of education');
    });
    
    // Check for child support accounts
    const hasChildSupport = availableAccounts.some(acc => {
      const name = (acc.creditor_name || acc.creditor || acc.name || '').toLowerCase();
      return name.includes('child support') || name.includes('dcss') || name.includes('cse');
    });
    
    // Check for auto loans
    const hasAutoLoans = availableAccounts.some(acc => {
      const name = (acc.creditor_name || acc.creditor || acc.name || '').toLowerCase();
      return name.includes('auto') || name.includes('car') || name.includes('vehicle') ||
             name.includes('toyota') || name.includes('honda') || name.includes('ford') ||
             name.includes('gm financial') || name.includes('ally') || name.includes('santander') ||
             name.includes('carmax') || name.includes('carvana');
    });
    
    // Auto-suggest student loans letter
    if (hasStudentLoans && disputeType === 'identity_theft') {
      suggestions['student_loans'] = true;
      autoExpandSections['identity_theft'] = true;
    }
    
    // Auto-suggest child support letter
    if (hasChildSupport && disputeType === 'identity_theft') {
      suggestions['child_support'] = true;
      autoExpandSections['identity_theft'] = true;
    }
    
    // Auto-suggest auto loan late payments letter
    if (hasAutoLoans && (disputeType === 'late_payments' || disputeType === 'inaccuracies')) {
      suggestions['auto_loan_lates'] = true;
      autoExpandSections['late_payments'] = true;
    }
    
    // Auto-suggest bankruptcy removal if hasBankruptcy is true
    if (hasBankruptcy) {
      suggestions['bankruptcy_removal'] = true;
      autoExpandSections['bankruptcy'] = true;
    }
    
    // Auto-suggest inquiry removal if there are inquiries
    if (availableInquiries.length > 0) {
      suggestions['inquiry_removal'] = true;
      autoExpandSections['inquiries'] = true;
    }
    
    // Only set if we have suggestions
    if (Object.keys(suggestions).length > 0) {
      setSelectedLetters(prev => ({ ...prev, ...suggestions }));
      setExpandedLetters(prev => ({ ...prev, ...suggestions }));
      setExpandedSections(prev => ({ ...prev, ...autoExpandSections }));
      
      // Show toast about auto-suggestions
      const count = Object.keys(suggestions).length;
      toast.info(`${count} supplemental letter${count > 1 ? 's' : ''} auto-suggested based on your accounts`, {
        duration: 4000
      });
    }
  }, []); // Run once on mount

  // Toggle letter selection
  const toggleLetterSelection = (letterId) => {
    setSelectedLetters(prev => ({
      ...prev,
      [letterId]: !prev[letterId]
    }));
    
    // Auto-expand when selected
    if (!selectedLetters[letterId]) {
      setExpandedLetters(prev => ({
        ...prev,
        [letterId]: true
      }));
    }
  };

  // Toggle account selection for a letter
  const toggleAccountForLetter = (letterId, accountId) => {
    setLetterAccounts(prev => {
      const current = prev[letterId] || [];
      if (current.includes(accountId)) {
        return { ...prev, [letterId]: current.filter(id => id !== accountId) };
      } else {
        return { ...prev, [letterId]: [...current, accountId] };
      }
    });
  };

  // Add manual account
  const addManualAccount = (letterId) => {
    setManualAccounts(prev => {
      const current = prev[letterId] || [];
      return {
        ...prev,
        [letterId]: [...current, { id: Date.now(), name: '', accountNumber: '', balance: '' }]
      };
    });
  };

  // Update manual account
  const updateManualAccount = (letterId, accountId, field, value) => {
    setManualAccounts(prev => {
      const current = prev[letterId] || [];
      return {
        ...prev,
        [letterId]: current.map(acc => 
          acc.id === accountId ? { ...acc, [field]: value } : acc
        )
      };
    });
  };

  // Remove manual account
  const removeManualAccount = (letterId, accountId) => {
    setManualAccounts(prev => {
      const current = prev[letterId] || [];
      return {
        ...prev,
        [letterId]: current.filter(acc => acc.id !== accountId)
      };
    });
  };

  // Get accounts for a specific letter based on its section
  const getAccountsForLetter = (letterId) => {
    const letterType = SUPPLEMENTAL_LETTER_TYPES[letterId];
    if (!letterType) return [];
    
    // For inquiry removal, return inquiries instead of accounts
    if (letterId === 'inquiry_removal') {
      return availableInquiries.map(inq => ({
        id: inq.id || `inq_${inq.company_name || inq.name}_${inq.inquiry_date || inq.date}`,
        name: inq.company_name || inq.name || inq.creditor,
        accountNumber: '',
        balance: '',
        type: 'inquiry',
        date: inq.inquiry_date || inq.date
      }));
    }
    
    // For other letters, return accounts (handle multiple formats from wizard)
    return availableAccounts.map(acc => ({
      id: acc.id || acc._id || `acc_${acc.creditor_name || acc.creditor}_${acc.balance}`,
      name: acc.creditor_name || acc.creditor || acc.name,
      accountNumber: acc.account_number || acc.accountNumber || '',
      balance: acc.balance || acc.balance_owed || acc.amount || '',
      date_opened: acc.date_opened || '',
      type: acc.account_type || acc.type || 'account'
    }));
  };

  // Generate a single letter
  const generateLetter = async (letterId) => {
    const letterType = SUPPLEMENTAL_LETTER_TYPES[letterId];
    if (!letterType) return;
    
    const selectedAccountIds = letterAccounts[letterId] || [];
    const manualAccountList = manualAccounts[letterId] || [];
    
    if (selectedAccountIds.length === 0 && manualAccountList.length === 0) {
      toast.error('Please select at least one account for this letter');
      return;
    }
    
    setGenerating(letterId);
    
    try {
      // Get full account details
      const allAccounts = getAccountsForLetter(letterId);
      const selectedFullAccounts = allAccounts.filter(acc => selectedAccountIds.includes(acc.id));
      
      // Combine with manual accounts
      const accountsForLetter = [
        ...selectedFullAccounts,
        ...manualAccountList.filter(acc => acc.name.trim())
      ];
      
      const response = await axios.post(`${API}/supplemental-letter/generate`, {
        client_id: clientId,
        letter_type: letterId,
        accounts: accountsForLetter,
        client_data: clientData,
        signature_data: signatureData
      });
      
      if (response.data.success) {
        setGeneratedLetters(prev => ({
          ...prev,
          [letterId]: {
            url: response.data.pdf_url,
            generated_at: new Date().toISOString(),
            accounts_count: accountsForLetter.length
          }
        }));
        
        toast.success(`${letterType.title} letter generated successfully!`);
        
        if (onLettersGenerated) {
          onLettersGenerated(letterId, response.data);
        }
      }
    } catch (error) {
      console.error('Error generating letter:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate letter');
    } finally {
      setGenerating(null);
    }
  };

  // Generate all selected letters
  const generateAllLetters = async () => {
    const lettersToGenerate = Object.entries(selectedLetters)
      .filter(([_, selected]) => selected)
      .map(([letterId]) => letterId);
    
    if (lettersToGenerate.length === 0) {
      toast.error('Please select at least one letter to generate');
      return;
    }
    
    for (const letterId of lettersToGenerate) {
      await generateLetter(letterId);
    }
  };

  // Download a generated letter
  const downloadLetter = async (letterId) => {
    const letter = generatedLetters[letterId];
    if (!letter?.url) return;
    
    try {
      const response = await axios.get(letter.url, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${SUPPLEMENTAL_LETTER_TYPES[letterId].title.replace(/\s+/g, '_')}_Letter.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download letter');
    }
  };

  // Count generated letters
  const generatedCount = Object.keys(generatedLetters).length;
  const selectedCount = Object.values(selectedLetters).filter(Boolean).length;

  // Determine which sections to show based on dispute type
  const visibleSections = Object.entries(LETTER_SECTIONS).filter(([sectionId, section]) => {
    // Always show bankruptcy if hasBankruptcy is true
    if (sectionId === 'bankruptcy' && hasBankruptcy) return true;
    
    // Show based on dispute type
    if (disputeType === 'identity_theft' && sectionId === 'identity_theft') return true;
    if (disputeType === 'identity_theft' && sectionId === 'inquiries') return true;
    if (disputeType === 'late_payments' && sectionId === 'late_payments') return true;
    if (disputeType === 'inaccuracies') return true; // Show all for general inaccuracies
    
    return false;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-3">
            <FileText className="w-7 h-7 text-amber-400" />
            Supplemental Dispute Letters
          </h2>
          <p className="text-zinc-400 mt-1">
            Add specialized letters to strengthen your dispute packet
          </p>
        </div>
        
        {selectedCount > 0 && (
          <Badge className="bg-amber-600 text-white px-3 py-1">
            {selectedCount} letter{selectedCount !== 1 ? 's' : ''} selected
          </Badge>
        )}
      </div>

      {/* Instructions */}
      <Card className="bg-gradient-to-r from-amber-900/20 to-zinc-900 border-amber-500/30">
        <CardContent className="pt-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-400 mt-0.5" />
            <div className="text-sm text-zinc-300">
              <p className="font-medium text-amber-400 mb-1">How to use:</p>
              <ol className="list-decimal list-inside space-y-1 text-zinc-400">
                <li>Select the letter type(s) you need</li>
                <li>Choose accounts from the dropdown OR add manually</li>
                <li>Click "Generate Letter" for each selected type</li>
                <li>Letters will be added to your dispute packet</li>
              </ol>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Letter Sections */}
      {visibleSections.map(([sectionId, section]) => (
        <Card key={sectionId} className={`bg-zinc-900 border-zinc-700 ${section.bgColor}`}>
          <CardHeader 
            className="cursor-pointer"
            onClick={() => setExpandedSections(prev => ({ ...prev, [sectionId]: !prev[sectionId] }))}
          >
            <CardTitle className={`flex items-center justify-between ${section.color}`}>
              <span className="flex items-center gap-2">
                {section.title}
                <Badge variant="outline" className="ml-2 text-xs">
                  {section.letters.length} letter{section.letters.length !== 1 ? 's' : ''}
                </Badge>
              </span>
              {expandedSections[sectionId] ? (
                <ChevronUp className="w-5 h-5" />
              ) : (
                <ChevronDown className="w-5 h-5" />
              )}
            </CardTitle>
            <p className="text-sm text-zinc-400">{section.description}</p>
          </CardHeader>
          
          {expandedSections[sectionId] && (
            <CardContent className="space-y-4">
              {section.letters.map(letterId => {
                const letterType = SUPPLEMENTAL_LETTER_TYPES[letterId];
                const Icon = letterType.icon;
                const isSelected = selectedLetters[letterId];
                const isExpanded = expandedLetters[letterId];
                const isGenerated = generatedLetters[letterId];
                const accountsForThis = getAccountsForLetter(letterId);
                const selectedAccountsCount = (letterAccounts[letterId] || []).length + 
                  (manualAccounts[letterId] || []).filter(a => a.name.trim()).length;
                
                return (
                  <div 
                    key={letterId}
                    className={`rounded-lg border-2 transition-all ${
                      isSelected 
                        ? `${letterType.borderColor} ${letterType.bgColor}` 
                        : 'border-zinc-700 bg-zinc-800/50'
                    }`}
                  >
                    {/* Letter Header */}
                    <div className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Checkbox
                          id={letterId}
                          checked={isSelected || false}
                          onCheckedChange={() => toggleLetterSelection(letterId)}
                          className="border-zinc-500"
                        />
                        <Icon className={`w-5 h-5 ${letterType.color}`} />
                        <div>
                          <Label 
                            htmlFor={letterId}
                            className="text-white font-medium cursor-pointer"
                          >
                            {letterType.title}
                          </Label>
                          <p className="text-xs text-zinc-400">{letterType.description}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {isGenerated && (
                          <Badge className="bg-green-600 text-white">
                            <CheckCircle2 className="w-3 h-3 mr-1" />
                            Generated
                          </Badge>
                        )}
                        
                        {isSelected && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setExpandedLetters(prev => ({ ...prev, [letterId]: !prev[letterId] }))}
                          >
                            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          </Button>
                        )}
                      </div>
                    </div>
                    
                    {/* Expanded Content - Account Selection */}
                    {isSelected && isExpanded && (
                      <div className="px-4 pb-4 space-y-4 border-t border-zinc-700 pt-4">
                        {/* Legal Codes */}
                        <div className="flex flex-wrap gap-1">
                          {letterType.codes.map(code => (
                            <Badge key={code} variant="outline" className="text-xs text-zinc-400">
                              {code}
                            </Badge>
                          ))}
                        </div>
                        
                        {/* Account Selection */}
                        <div>
                          <Label className="text-zinc-300 text-sm mb-2 block">
                            Select Accounts ({selectedAccountsCount} selected)
                          </Label>
                          <div className="max-h-48 overflow-y-auto space-y-2 bg-zinc-800/50 rounded-lg p-3">
                            {accountsForThis.length > 0 ? (
                              accountsForThis.map(account => (
                                <div 
                                  key={account.id}
                                  className="flex items-center gap-2 p-2 rounded hover:bg-zinc-700/50"
                                >
                                  <Checkbox
                                    checked={(letterAccounts[letterId] || []).includes(account.id)}
                                    onCheckedChange={() => toggleAccountForLetter(letterId, account.id)}
                                  />
                                  <span className="text-sm text-white flex-1">{account.name}</span>
                                  {account.date_opened && (
                                    <span className="text-xs text-zinc-400">{account.date_opened}</span>
                                  )}
                                  {account.accountNumber && (
                                    <span className="text-xs text-zinc-500">#{account.accountNumber}</span>
                                  )}
                                  {account.balance && !isNaN(parseFloat(account.balance)) && parseFloat(account.balance) > 0 && (
                                    <span className="text-xs text-zinc-400">${account.balance}</span>
                                  )}
                                </div>
                              ))
                            ) : (
                              <p className="text-sm text-zinc-500 text-center py-2">
                                No accounts available. Add manually below.
                              </p>
                            )}
                          </div>
                        </div>
                        
                        {/* Manual Account Entry */}
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <Label className="text-zinc-300 text-sm">Manual Accounts</Label>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => addManualAccount(letterId)}
                              className="text-xs"
                            >
                              <Plus className="w-3 h-3 mr-1" />
                              Add Account
                            </Button>
                          </div>
                          
                          {(manualAccounts[letterId] || []).map(account => (
                            <div key={account.id} className="flex gap-2 mb-2">
                              <Input
                                placeholder="Creditor/Account Name"
                                value={account.name}
                                onChange={(e) => updateManualAccount(letterId, account.id, 'name', e.target.value)}
                                className="flex-1 bg-zinc-800 border-zinc-600 text-sm"
                              />
                              <Input
                                placeholder="Account #"
                                value={account.accountNumber}
                                onChange={(e) => updateManualAccount(letterId, account.id, 'accountNumber', e.target.value)}
                                className="w-32 bg-zinc-800 border-zinc-600 text-sm"
                              />
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => removeManualAccount(letterId, account.id)}
                                className="text-red-400 hover:text-red-300"
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          ))}
                        </div>
                        
                        {/* Generate Button */}
                        <div className="flex gap-2 pt-2">
                          <Button
                            onClick={() => generateLetter(letterId)}
                            disabled={generating === letterId || selectedAccountsCount === 0}
                            className="bg-amber-600 hover:bg-amber-700"
                          >
                            {generating === letterId ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Generating...
                              </>
                            ) : (
                              <>
                                <FileText className="w-4 h-4 mr-2" />
                                Generate Letter
                              </>
                            )}
                          </Button>
                          
                          {isGenerated && (
                            <Button
                              variant="outline"
                              onClick={() => downloadLetter(letterId)}
                            >
                              <Download className="w-4 h-4 mr-2" />
                              Download
                            </Button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </CardContent>
          )}
        </Card>
      ))}

      {/* Summary & Continue */}
      <Card className="bg-zinc-900 border-zinc-700">
        <CardContent className="pt-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white font-medium">
                {generatedCount > 0 ? (
                  <>
                    <CheckCircle2 className="w-4 h-4 inline mr-2 text-green-400" />
                    {generatedCount} supplemental letter{generatedCount !== 1 ? 's' : ''} generated
                  </>
                ) : (
                  <span className="text-zinc-400">
                    No supplemental letters generated yet
                  </span>
                )}
              </p>
              <p className="text-xs text-zinc-500 mt-1">
                These letters will be added to your dispute packet
              </p>
            </div>
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => onComplete && onComplete()}
                className="border-zinc-600"
              >
                Skip
              </Button>
              <Button
                onClick={() => {
                  if (generatedCount > 0) {
                    onComplete && onComplete();
                  } else {
                    toast.info('Generate at least one letter or click Skip to continue');
                  }
                }}
                className="bg-green-600 hover:bg-green-700"
              >
                Continue
                <ChevronDown className="w-4 h-4 ml-2 rotate-[-90deg]" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
